import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Services } from '../../assets';
import gsap from 'gsap';

const leftItems = [
  {
    number: 1,
    title: 'Personal Branding & Social Media',
    desc: 'Build your brand, be visible & Relevant',
  },
  {
    number: 2,
    title: 'Communication Skills',
    desc: 'Learn how to talk like the Pros & use AI to back it up.',
  },
  {
    number: 3,
    title: 'Confidence Building',
    desc: 'Handle rejections. Hold your ground. Lead the room.',
  },
  {
    number: 4,
    title: 'Mindset Mapping',
    desc: 'Train your brain for long-term real estate success.',
  },
  {
    number: 5,
    title: 'Advanced Presentation & Negotiation Techniques',
    desc: 'Speak smooth. Close smart. Earn every ‘yes.’',
  },
  {
    number: 6,
    title: 'NLP & Influencing Skills for Real Estate Sales',
    desc: 'Lead from the heart, follow through with Intent, Influence with ease.',
  },
];

const bonusItem = {
  number: 7,
  title: 'Personal Brand Accelerator & Confidence Intensive',
  desc: 'Build a brand. Boost your voice. Break your limits.',
};

const finalItem = {
  title: 'Final Simulation Round',
  desc: 'Real buyers. Real objections. Real confidence.',
};

const TrainingTabs: React.FC = () => {
  const [tab, setTab] = React.useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const finalStepRef = useRef<HTMLDivElement>(null);
  const leftStepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const bonusStepRef = useRef<HTMLDivElement>(null);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  useEffect(() => {
    // Intersection Observer for section
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  useEffect(() => {
    if (visible) {
      // Animate left steps
      if (leftStepRefs.current.length) {
        gsap.fromTo(
          leftStepRefs.current,
          { opacity: 0, x: -40 },
          {
            opacity: 1,
            x: 0,
            duration: 1,
            stagger: 0.12,
            ease: 'power3.out',
          }
        );
      }
      // Animate right steps (bonus and final) if tab === 1
      if (tab === 1) {
        if (bonusStepRef.current) {
          gsap.fromTo(
            bonusStepRef.current,
            { opacity: 0, x: 40 },
            { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out' }
          );
        }
        if (finalStepRef.current) {
          gsap.fromTo(
            finalStepRef.current,
            { opacity: 0, y: 40, scale: 0.95 },
            { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'power3.out' }
          );
        }
      }
    }
  }, [visible, tab]);

  return (
    <Box sx={{ position: 'relative', zIndex: 2 }}>
      {/* Bottom Image */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <img
          src="https://dprstorage.b-cdn.net/RW/hrsbgimg.png"
          alt="Bottom Decoration"
          style={{ width: '100%', display: 'block' }}
        />
      </Box>
      {/* Main Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Box>
          <Box >
            {/* Top Section - Light Blue */}
            <Box sx={{ backgroundColor: 'rgba(15, 99, 165, 0.4)', py: 4 }}>
              <Grid container spacing={3} justifyContent="center">
                {/* Date */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle1"
                    align="center"
                    sx={{ mb: 1 }}
                  >
                    Date:
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid #0f63a5',
                      borderRadius: '8px',
                      px: 4,
                      py: 1.5,
                      fontWeight: 'bold',
                      fontSize: { xs: '1rem', md: '1.25rem' },
                      textAlign: 'center',
                      backgroundColor: '#fff',
                      maxWidth: '240px',
                      margin: '0 auto',
                      color: 'black',
                      display: 'none'
                    }}
                  >
                    To be Announced
                  </Box>
                </Grid>

                {/* Duration */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle1"
                    align="center"
                    sx={{ mb: 1 }}
                  >
                    Duration:
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid #0f63a5',
                      borderRadius: '8px',
                      px: 4,
                      py: 1.5,
                      fontWeight: 'bold',
                      fontSize: { xs: '1rem', md: '1.25rem' },
                      textAlign: 'center',
                      backgroundColor: '#fff',
                      maxWidth: '240px',
                      margin: '0 auto',
                      color: 'black'
                    }}
                  >
                    4 Hours / 6 Hours
                  </Box>
                </Grid>

                {/* Venue */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle1"
                    align="center"
                    sx={{ mb: 1 }}
                  >
                    Venue:
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid #0f63a5',
                      borderRadius: '8px',
                      px: 4,
                      py: 1.5,
                      fontWeight: 'bold',
                      fontSize: { xs: '1rem', md: '1.25rem' },
                      textAlign: 'center',
                      backgroundColor: '#fff',
                      maxWidth: '240px',
                      margin: '0 auto',
                      color: 'black'
                    }}
                  >
                    Hyderabad{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontWeight: 400 }}
                    >
                      (Classroom)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Attached White Section */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >

              <Box
                sx={{
                  backgroundColor: 'rgba(15, 99, 165, 0.4)',
                  borderBottomLeftRadius: '20px',
                  borderBottomRightRadius: '20px',
                  // mt: '-20px',
                  pt: 4,
                  pb: 2,
                  textAlign: 'center',
                  width: { xs: '80%', md: '40%', lg: '40%' },

                }}
              >
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{ color: '#000' }}
                >
                  What You Will Learn In
                </Typography>
              </Box>
            </Box>
          </Box>
                
          {/* Tabs Centered */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, mt: 3 }}>
            <Tabs
              className='akirafont !important'
              value={tab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"


            >
              <Tab
                className='akirafont !important'
                label="4 HRS"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', md: '2.5rem', lg: '2.5rem' },
                  textTransform: 'none',
                  mx: { xs: 2, md: 6, lg: 12 },
                  fontFamily: " 'Akira', sans-serif !important"
                }}
              />
              <Tab
                className='akirafont !important'
                label="6 HRS"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', md: '2.5rem', lg: '2.5rem' },
                  textTransform: 'none',
                  mx: { xs: 2, md: 6, lg: 12 },
                  fontFamily: " 'Akira', sans-serif !important"
                }}
              />
            </Tabs>
          </Box>

          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="center"
            mb={8}
            ref={sectionRef}
            sx={{
              px: 4
            }}
          >
            {/* LEFT COLUMN - always shown */}
            <Grid item xs={12} md={6}>
              {(tab === 0 ? leftItems.slice(0, 4) : leftItems).map((item, idx) => (
                <Box
                  key={item.number}
                  ref={(el) => {
                    leftStepRefs.current[idx] = el as HTMLDivElement | null;
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 3,
                    py: 2,
                    my: 1,
                    background: 'rgba(15, 99, 165, 0.4)'
                  }}
                >
                  <Box
                    className='akirafont'
                    sx={{
                      fontSize: 36,
                      fontWeight: 'bold',
                      color: '#fff',
                      mr: 2,
                      minWidth: 30,
                    }}
                  >
                    {item.number}
                  </Box>
                  <Box>
                    <Typography variant='h6' fontWeight="bold" sx={{ textAlign: 'start' }}>{item.title}</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'start' }}>{item.desc}</Typography>
                  </Box>
                </Box>
              ))}

            </Grid>

            {/* PLUS ICON & BONUS - only in 6 HRS */}
            {tab === 1 && (
              <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="textPrimary"
                  gutterBottom
                >
                  Bonus
                </Typography>
                {/* Add icon and Bonus Box in a row */}
                <Box
                  ref={bonusStepRef}
                  sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      width: { xs: 16, md: 30 },
                      height: { xs: 16, md: 30 },
                    }}
                  >
                    <img
                      src={Services.addicon}
                      alt="Add"
                      style={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 3,
                      py: 2,
                      background: 'rgba(15, 99, 165, 0.4)'
                    }}
                  >
                    <Box
                      className='akirafont'
                      sx={{
                        fontSize: 36,
                        fontWeight: 'bold',
                        color: '#fff',
                        mr: 2,
                        minWidth: 30,
                      }}
                    >
                      {bonusItem.number}
                    </Box>
                    <Box>
                      <Typography variant='h6' fontWeight="bold" sx={{ textAlign: 'start' }}>{bonusItem.title}</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'start' }}>{bonusItem.desc}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Final Point (no box) */}
                <Box
                  ref={finalStepRef}
                  sx={{
                    mt: 6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 16, md: 28 },
                      height: { xs: 16, md: 28 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      mt: { xs: 0.5, md: 0.5 },
                    }}
                  >
                    <img
                      src={Services.addicon}
                      alt="Add"
                      style={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                  <Box>
                    <Typography variant='h6' fontWeight="bold" sx={{ textAlign: 'start' }}>{finalItem.title}</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'start' }}>{finalItem.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default TrainingTabs;

